import { useAgents } from '#~/agent/hooks.js';
import { useBrandNameFromModelId } from '#~/device/registry/hooks.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import * as Dialog from '@studio/design-system/dialog';
import { Icon } from '@studio/design-system/icon';
import { Spinner } from '@studio/design-system/spinner';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { useStore } from '../store/hooks.js';
import { Actions } from './actions.jsx';
import type { Props } from './types.js';

/** `<Header>` component. */
export const Header = ({ className, ...props }: Props): ReactElement => {
  const { enabled: isBridgeEnabled } = useFeatureFlag(PostHogFeatureFlag.BRIDGE_FEATURE, {
    defaultValue: false
  });

  const candidate = useStore((µ) => µ.candidate);
  const agentId = useStore((µ) => µ.agentId);
  const agent = useAgents((a) => a.find((a) => a.id === agentId));

  const brandName = useBrandNameFromModelId(candidate.modelId);
  const isLoading = brandName === undefined;
  const brandAndModelName = brandName ? `${brandName}:${candidate.name}` : candidate.name;

  return (
    <Dialog.Header
      className={twMerge('border-0', className)}
      title={
        <div className='flex flex-col'>
          <p className='text-lg font-bold text-light'>
            {isLoading ? (
              <span className='flex items-center text-xl font-bold text-light'>
                <Spinner className='mr-4 text-md' />
                Loading...
              </span>
            ) : (
              brandAndModelName
            )}
          </p>
          {isBridgeEnabled && (
            <div className='flex items-center'>
              <Icon icon='bridge' className='mr-4 mt-2 text-md text-primary' />
              <span className='text-md text-primary'>{agent?.name || 'Loading...'}</span>
            </div>
          )}
        </div>
      }
      {...props}
    >
      <Actions className='ml-auto mr-24' />
    </Dialog.Header>
  );
};
